import { InvestorDetailsPacingTabCommitmentPlansQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  DELETE_COMMITMENT_PLAN_DIALOG_COMMITMENT_PLANS_QUERY,
  DELETE_COMMITMENT_PLAN_MUTATION,
  UPDATE_COMMITMENT_PLAN_MUTATION
} from '../../queries';

interface DeletePlanProps {
  isOpen: boolean;
  currentRow: InvestorDetailsPacingTabCommitmentPlansQuery['commitmentPlans']['data'][number] | null;
  refetch: () => void;
  onClose: () => void;
}

const DeletePlan = ({ isOpen, currentRow, refetch, onClose }: DeletePlanProps) => {
  const params = useParams();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [commitmentPlanSelectOptions, setCommitmentPlanSelectOptions] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deleteCommitmentPlan, { loading: deleteCommitmentPlanLoading }] = useMutation(DELETE_COMMITMENT_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  });

  const [updateCommitmentPlan, { loading: updateCommitmentPlanLoading }] = useMutation(UPDATE_COMMITMENT_PLAN_MUTATION, {
    onError: (error) => {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  });

  const { data: commitmentPlansData, loading: commitmentPlansLoading } = useQuery(DELETE_COMMITMENT_PLAN_DIALOG_COMMITMENT_PLANS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !params?.entityId,
    variables: {
      input: {
        legalEntityId: params?.entityId,
        limit: 1000
      }
    },
    onCompleted: (data) => {
      setCommitmentPlanSelectOptions(
        data.commitmentPlans.data.map((plan) => ({ label: plan.name, value: plan.id })).filter((item) => item.value !== currentRow.id)
      );
    }
  });

  const deletePlan = () => {
    setErrorMessage(null);

    if (commitmentPlansData?.commitmentPlans.total > 1 && currentRow.active) {
      updateCommitmentPlan({
        variables: {
          input: {
            id: selectedPlanId,
            active: true
          }
        },
        onCompleted: () => {
          deleteCommitmentPlan({
            variables: {
              id: currentRow.id
            },
            onCompleted: () => {
              refetch();
              onClose();
            }
          });
        }
      });
      return;
    }
    deleteCommitmentPlan({
      variables: {
        id: currentRow.id
      },
      onCompleted: () => {
        refetch();
        onClose();
      }
    });
  };

  if (!currentRow) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Commitment Plan"
      confirmButton={{
        name: 'Confirm',
        disabled:
          (commitmentPlansData?.commitmentPlans.total > 1 && currentRow.active && !selectedPlanId) ||
          commitmentPlansLoading ||
          updateCommitmentPlanLoading ||
          deleteCommitmentPlanLoading,
        onClick: deletePlan
      }}
      showCloseIcon={!updateCommitmentPlanLoading && !deleteCommitmentPlanLoading}
      showCancelButton={!updateCommitmentPlanLoading && !deleteCommitmentPlanLoading}
      isUseOutsideClick={!updateCommitmentPlanLoading && !deleteCommitmentPlanLoading}
    >
      {updateCommitmentPlanLoading || deleteCommitmentPlanLoading ? (
        <ModalLoader />
      ) : (
        <ModalBody>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <SubTitle>
            Please confirm that you want to permanently delete this commitment Plan (<span style={{ fontWeight: 700 }}>{currentRow.name}</span>). This
            action cannot be undone.
          </SubTitle>
          {commitmentPlansData?.commitmentPlans.total > 1 && currentRow.active && (
            <Select
              value={selectedPlanId}
              options={[{ label: 'Select another plan', value: '' }, ...commitmentPlanSelectOptions]}
              setValue={setSelectedPlanId}
              defaultSelectLabel="Select another plan"
              label="Select one of the remaining plans to make it the active commitment plan"
              isRequiredField
            />
          )}
        </ModalBody>
      )}
    </ModalWrappedContent>
  );
};

export default DeletePlan;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 35px;
`;

const SubTitle = styled.p`
  text-align: center;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.base')};
`;
